import { AuthLevelRole } from "./auth-level-role";
import { MYDRAEGER_RIGHTS } from "./mydraeger-rights";

const GLOBAL_ADMIN_RIGHT = "N008";
const USER_RIGHT = "N009";
const CKAM_PREFIX = "CountryKeyAccountManager_";

/**
 * Maps a myDräger rights array to a legacy-compatible authLevel role object.
 *
 * This allows the frontend to continue using its existing role-based logic
 * (e.g. `user.role["user"] === "globalAdmin"`) while receiving rights from
 * myDräger tokens instead of legacy IDMS authLevel.
 *
 * @param rights - Array of right IDs from myDräger token (e.g. ["N000", "N008", "N009"])
 * @returns A Record<string, string> in the same format as the legacy authLevel object.
 *
 * Examples:
 *   ["N000", "N008", "N009"] → { user: "globalAdmin" }
 *   ["N000", "N009", "N010"] → { user: "user", "user/DE": "countryKeyAccountManager" }
 *   ["N000", "N009"]         → { user: "user" }
 *   []                       → {}
 */
export function mapRightsToAuthRole(rights: string[]): Record<string, string> {
  if (!rights || rights.length === 0) {
    return {};
  }

  if (rights.includes(GLOBAL_ADMIN_RIGHT)) {
    return { user: AuthLevelRole.GLOBAL_ADMIN };
  }

  const countryCodes = extractCountryCodesFromRights(rights);
  if (countryCodes.length > 0) {
    const role: Record<string, string> = { user: AuthLevelRole.USER };
    for (const cc of countryCodes) {
      role[`user/${cc}`] = AuthLevelRole.COUNTRY_KEY_ACCOUNT_MANAGER;
    }
    return role;
  }

  if (rights.includes(USER_RIGHT)) {
    return { user: AuthLevelRole.USER };
  }

  return {};
}

/**
 * Extracts country codes from CKAM rights in the rights array.
 * E.g. right "N010" → name "CountryKeyAccountManager_DE" → country "DE"
 */
function extractCountryCodesFromRights(rights: string[]): string[] {
  return MYDRAEGER_RIGHTS.filter((r) => rights.includes(r.id) && r.name.startsWith(CKAM_PREFIX)).map((r) =>
    r.name.replace(CKAM_PREFIX, ""),
  );
}
